/**
 * Premium Invite — página de invitación global que usa BioTemplatePremiumSimple.
 *
 * El Cloudflare Worker inyecta window.__TEMPLATE_CONFIG__ con el país y el idioma
 * detectados via geo (request.cf.country). Esta página los lee y deriva el idioma.
 *
 * Si hay ?code= o ?ref= en la URL:
 *   1. Valida el código contra /api/invitation-codes/validate
 *   2a. Si es inválido del todo (not_found / inactive) → pantalla de error duro
 *   2b. Si expiró o se agotó (expired / exhausted) → pantalla suave con CTA a /acceso
 *   3. Si es válido y tiene poco tiempo → countdown sticky encima del template
 *   4. Si es válido sin urgencia → muestra el template normalmente
 *
 * Sin código → muestra BioTemplatePremiumSimple directamente (acceso abierto).
 *
 * BioTemplatePremiumSimple decide el checkout según país:
 *   AR → directo a VITE_MP_CHECKOUT_BASIC_AR / VITE_MP_CHECKOUT_VIP_AR (env vars Railway)
 *   Resto → Stripe via /api/create-subscription-checkout
 */

import BioTemplatePremiumSimple from "@/components/templates/BioTemplatePremiumSimple";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Clock, Loader2 } from "lucide-react";

// Mostrar countdown solo cuando quedan menos de 48 h
const COUNTDOWN_THRESHOLD_SECONDS = 48 * 3600;

type CodeState = "idle" | "checking" | "valid" | "invalid_soft" | "invalid_hard";
type InvalidReason = "expired" | "exhausted" | "inactive" | "not_found";

interface CountdownTick {
  h: number;
  m: number;
  s: number;
}

function useCountdown(deadlineMs: number | null): CountdownTick | null {
  const [tick, setTick] = useState<CountdownTick | null>(null);
  const rafRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!deadlineMs) { setTick(null); return; }

    const update = () => {
      const diff = deadlineMs - Date.now();
      if (diff <= 0) {
        setTick({ h: 0, m: 0, s: 0 });
        return;
      }
      setTick({
        h: Math.floor(diff / 3_600_000),
        m: Math.floor((diff % 3_600_000) / 60_000),
        s: Math.floor((diff % 60_000) / 1_000),
      });
      rafRef.current = setTimeout(update, 1_000);
    };

    update();
    return () => { if (rafRef.current) clearTimeout(rafRef.current); };
  }, [deadlineMs]);

  return tick;
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

export default function PremiumInvite() {
  const { country, language } = useTemplateSelection();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const code = (searchParams.get("code") || searchParams.get("ref") || "").trim();

  const [codeState, setCodeState] = useState<CodeState>(code ? "checking" : "idle");
  const [invalidMessage, setInvalidMessage] = useState<string>(
    t("premiumInvite.defaultInvalidMessage"),
  );
  const [invalidReason, setInvalidReason] = useState<InvalidReason | null>(null);
  const [deadlineMs, setDeadlineMs] = useState<number | null>(null);
  const countdown = useCountdown(deadlineMs);

  // Validate code once on mount
  useEffect(() => {
    if (!code) return;

    let cancelled = false;

    const validate = async () => {
      try {
        const res = await fetch(
          `/api/invitation-codes/validate?code=${encodeURIComponent(code.toUpperCase())}`,
        );
        const data: {
          valid: boolean;
          reason?: InvalidReason;
          message?: string;
          valid_for_seconds?: number;
          valid_until_iso?: string;
        } = await res.json().catch(() => ({ valid: false }));

        if (cancelled) return;

        if (!data.valid) {
          const reason: InvalidReason = data.reason ?? "not_found";
          setInvalidReason(reason);
          setInvalidMessage(
            data.message?.trim() ||
              (reason === "expired" || reason === "exhausted"
                ? t("premiumInvite.softFallbackMessage")
                : t("premiumInvite.hardFallbackMessage")),
          );
          // expired / exhausted → pantalla suave (el usuario puede seguir al checkout)
          // inactive / not_found → pantalla dura (el código no debería usarse)
          setCodeState(
            reason === "expired" || reason === "exhausted" ? "invalid_soft" : "invalid_hard",
          );
          return;
        }

        setCodeState("valid");

        // Countdown solo si queda poco tiempo
        const secs = data.valid_for_seconds;
        if (secs !== undefined && secs > 0 && secs < COUNTDOWN_THRESHOLD_SECONDS) {
          setDeadlineMs(Date.now() + secs * 1_000);
        } else if (data.valid_until_iso) {
          const until = new Date(data.valid_until_iso).getTime();
          const remaining = (until - Date.now()) / 1_000;
          if (remaining > 0 && remaining < COUNTDOWN_THRESHOLD_SECONDS) {
            setDeadlineMs(until);
          }
        }
      } catch {
        // Red error → mostrar template de todas formas (soft fail)
        if (!cancelled) setCodeState("valid");
      }
    };

    validate();
    return () => { cancelled = true; };
  }, [code]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (codeState === "checking") {
    return (
      <div
        className="h-dvh flex items-center justify-center bg-black text-white"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-purple-400" />
          <p className="text-gray-400 text-sm">{t("premiumInvite.verifying")}</p>
        </div>
      </div>
    );
  }

  // ── Expirado / agotado — pantalla suave ────────────────────────────────────
  // El link murió, pero el usuario puede ver los planes igual (acceso siempre abierto).
  if (codeState === "invalid_soft") {
    return (
      <div
        className="h-dvh flex items-center justify-center bg-black text-white px-6"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 bg-gradient-to-br from-amber-900/20 via-black to-black" />
        <div className="relative z-10 max-w-sm w-full text-center space-y-5">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/20 flex items-center justify-center">
            <Clock className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-xl font-bold">
            {invalidReason === "exhausted"
              ? t("premiumInvite.exhaustedTitle")
              : t("premiumInvite.expiredTitle")}
          </h1>
          <p className="text-gray-400 text-sm leading-relaxed">{invalidMessage}</p>

          {/* CTA principal: ir a /acceso sin código (siempre abierto) */}
          <a
            href="/acceso"
            className="inline-block w-full py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 rounded-xl text-sm font-bold transition-opacity"
          >
            {t("premiumInvite.viewPlans")}
          </a>

          <a
            href="/"
            className="inline-block text-sm text-gray-500 hover:text-gray-300 transition-colors"
          >
            {t("premiumInvite.backHome")}
          </a>
        </div>
      </div>
    );
  }

  // ── Inválido de verdad (código inexistente o desactivado) — pantalla dura ──
  if (codeState === "invalid_hard") {
    return (
      <div
        className="h-dvh flex items-center justify-center bg-black text-white px-6"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 bg-gradient-to-br from-red-900/30 via-black to-black" />
        <div className="relative z-10 max-w-sm w-full text-center space-y-5">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-red-500/20 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold">{t("premiumInvite.invalidTitle")}</h1>
          <p className="text-gray-400 text-sm leading-relaxed">{invalidMessage}</p>
          <a
            href="/"
            className="inline-block mt-2 px-6 py-3 bg-white/10 hover:bg-white/15 rounded-xl text-sm font-semibold transition-colors"
          >
            {t("premiumInvite.backHome")}
          </a>
        </div>
      </div>
    );
  }

  // ── Valid (idle = sin código, valid = código ok) ───────────────────────────
  const showCountdown = countdown !== null && deadlineMs !== null;

  return (
    <div className="relative">
      {/* Countdown sticky — solo cuando el link tiene tiempo limitado */}
      {showCountdown && (
        <div
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2.5 px-4 py-2.5 text-sm font-bold text-black"
          style={{
            background: "linear-gradient(90deg, #f59e0b 0%, #f97316 100%)",
            fontFamily: FONT_FAMILY,
          }}
        >
          <Clock className="w-4 h-4 shrink-0" />
          <span>
            {t("premiumInvite.countdownLabel")}{" "}
            <span className="tabular-nums tracking-tight">
              {countdown.h > 0
                ? `${pad(countdown.h)}:${pad(countdown.m)}:${pad(countdown.s)}`
                : `${pad(countdown.m)}:${pad(countdown.s)}`}
            </span>
          </span>
        </div>
      )}

      <div style={showCountdown ? { paddingTop: "2.5rem" } : undefined}>
        <BioTemplatePremiumSimple
          country={country}
          language={language}
          deepBackgroundScrim
          inviteCode={code || undefined}
        />
      </div>
    </div>
  );
}
